import { IAction } from '@faire/web-api--source/indigofair/data/IAction';
import { IBackgroundIcon } from '@faire/web-api--source/indigofair/data/IBackgroundIcon';
import { IButtonElement } from '@faire/web-api--source/indigofair/data/IButtonElement';
import { IGridStyle } from '@faire/web-api--source/indigofair/data/IGridStyle';
import { IResponsiveBackgroundIcon } from '@faire/web-api--source/indigofair/data/IResponsiveBackgroundIcon';
import { IResponsiveInt } from '@faire/web-api--source/indigofair/data/IResponsiveInt';
import { IResponsiveTextElement } from '@faire/web-api--source/indigofair/data/IResponsiveTextElement';
import { ITextElement } from '@faire/web-api--source/indigofair/data/ITextElement';
import { ITile } from '@faire/web-api--source/indigofair/data/ITile';

export namespace IGridComponent {
  /**
   * The tags are out of order. All tag numbers must be unique. When adding fields, also update the field below and the tag number.
   * See https://www.notion.so/faire/Protocol-Buffers-Protos-ba527d2f70c34ab983b8d0417c6a56ac#6a792ceb9d37498795a117e712b9e7e7 for details.
   * The last tag added was `responsiveTitle`. The next tag number is 13.
   */
  export enum GridDensity {
    GRID_DENSITY_UNKNOWN = 'GRID_DENSITY_UNKNOWN',
    /**
     * @deprecated
     */
    FLEXIBLE = 'FLEXIBLE',
    /**
     * Tiles per row given viewport: 3 -> 5 -> 8
     * @deprecated
     */
    DENSE = 'DENSE',
    /**
     * Tiles per row given viewport: 2 -> 4 -> 6
     * @deprecated
     */
    STANDARD = 'STANDARD',
    /**
     * Tiles per row given viewport: 1 -> 2 -> 3
     * @deprecated
     */
    SPARSE = 'SPARSE',
  }

  export namespace IColumnLayout {
    /**
     * Return an instance of IColumnLayout with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (partial?: Partial<IColumnLayout>): IColumnLayout =>
      ({
        ...(partial ?? {}),
      }) as IColumnLayout;
  }

  export interface IColumnLayout {
    /**
     * Represents a column layout that has a fixed number of columns defined by one of the numColumns or
     * responsiveNumColumns fields in FixedColumnLayout.
     */
    fixed: IGridComponent.IFixedColumnLayout | undefined;
    /**
     * Represents a column layout where the number of columns in the grid is determined by the client
     * based on the grid tile container and the widths of the tile elements. The client will render as many columns
     * as possible to fill up the grid tile container's horizontal area with the tiles.
     */
    auto: IGridComponent.AutoColumnLayout | undefined;
  }

  export namespace IFixedColumnLayout {
    /**
     * Return an instance of IFixedColumnLayout with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (
      partial?: Partial<IFixedColumnLayout>
    ): IFixedColumnLayout =>
      ({
        ...(partial ?? {}),
      }) as IFixedColumnLayout;
  }

  /**
   * Represents the number of columns that the grid will have regardless of viewport size
   */
  export interface IFixedColumnLayout {
    num_columns: number | undefined;
    responsive_num_columns: IResponsiveInt | undefined;
  }

  export type AutoColumnLayout = unknown;
  export namespace IResponsiveTruncationBehaviour {
    /**
     * Return an instance of IResponsiveTruncationBehaviour with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (
      partial?: Partial<IResponsiveTruncationBehaviour>
    ): IResponsiveTruncationBehaviour =>
      ({
        ...(partial ?? {}),
      }) as IResponsiveTruncationBehaviour;
  }

  export interface IResponsiveTruncationBehaviour {
    desktop_and_above: IGridComponent.ITruncationBehaviour | undefined;
    tablet_and_above: IGridComponent.ITruncationBehaviour | undefined;
    mobile_and_above: IGridComponent.ITruncationBehaviour | undefined;
  }

  export namespace ITruncationBehaviour {
    /**
     * Return an instance of ITruncationBehaviour with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (
      partial?: Partial<ITruncationBehaviour>
    ): ITruncationBehaviour =>
      ({
        ...(partial ?? {}),
      }) as ITruncationBehaviour;
  }

  export interface ITruncationBehaviour {
    none: IGridComponent.NoTruncation | undefined;
    clip: IGridComponent.IClippedTruncationBehaviour | undefined;
    expand: IGridComponent.IExpandTruncationBehaviour | undefined;
  }

  export type NoTruncation = unknown;
  export namespace IClippedTruncationBehaviour {
    /**
     * Return an instance of IClippedTruncationBehaviour with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (
      partial?: Partial<IClippedTruncationBehaviour>
    ): IClippedTruncationBehaviour =>
      ({
        ...(partial ?? {}),
      }) as IClippedTruncationBehaviour;
  }

  export interface IClippedTruncationBehaviour {
    max_rows: number | undefined;
  }

  export namespace IExpandTruncationBehaviour {
    /**
     * Return an instance of IExpandTruncationBehaviour with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (
      partial?: Partial<IExpandTruncationBehaviour>
    ): IExpandTruncationBehaviour =>
      ({
        on_click: [],
        ...(partial ?? {}),
      }) as IExpandTruncationBehaviour;
  }

  export interface IExpandTruncationBehaviour {
    collapsed_max_rows: number | undefined;
    variant: keyof typeof IButtonElement.ButtonVariant | undefined;
    on_click: Array<IAction>;
  }

  export namespace ILogTileBatchView {
    /**
     * Return an instance of ILogTileBatchView with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (
      partial?: Partial<ILogTileBatchView>
    ): ILogTileBatchView =>
      ({
        parameters: [],
        ...(partial ?? {}),
      }) as ILogTileBatchView;
  }

  export interface ILogTileBatchView {
    event_name: string | undefined;
    /**
     * @protobufOption (faire.common.diffIgnore) = ALWAYS
     */
    parameters: Array<string>;
  }

  /**
   * Return an instance of IGridComponent with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (partial?: Partial<IGridComponent>): IGridComponent =>
    ({
      tiles: [],
      ...(partial ?? {}),
    }) as IGridComponent;
}

/**
 * Grid Component
 */
export interface IGridComponent {
  style: IGridStyle | undefined;
  tiles: Array<ITile>;
  title: ITextElement | undefined;
  responsive_title: IResponsiveTextElement | undefined;
  /**
   * Deprecated, please use `columnLayout` instead.
   * @deprecated
   */
  density: keyof typeof IGridComponent.GridDensity | undefined;
  truncation_behaviour: IGridComponent.ITruncationBehaviour | undefined;
  responsive_truncation_behaviour:
    | IGridComponent.IResponsiveTruncationBehaviour
    | undefined;
  /**
   * When set, clients will log the specified event whenever the set of visible tiles changes
   * the final parameter of this event will be a list of the visible tiles' [instanceIdentifier]
   * Note that this field is temporary and will be removed once we have fully migrated to
   * using product tile level impression events and deprecated the legacy batch events.
   */
  log_tile_batch_view: IGridComponent.ILogTileBatchView | undefined;
  /**
   * Represents the type of column layout used by the grid
   */
  column_layout: IGridComponent.IColumnLayout | undefined;
  /**
   * ctaButton rendered on the right side of the Grid component header
   */
  cta_element: IButtonElement | undefined;
  /**
   * Contains the information and positioning data of the icon
   * displayed in the background of the header of the grid component
   */
  header_background_icon: IBackgroundIcon | undefined;
  /**
   * Contains the information and positioning data of the responsive icon
   * displayed in the background of the header of the grid component.
   * This allows us to change the image's size/positioning based on the viewport
   */
  responsive_header_background_icon: IResponsiveBackgroundIcon | undefined;
}
